export function calculateAreaBbox(area) {
    let west = Infinity, south = Infinity, east = -Infinity, north = -Infinity;
    const visit = (value) => {
        if (!Array.isArray(value))
            return;
        if (value.length >= 2 && typeof value[0] === "number" && typeof value[1] === "number") {
            west = Math.min(west, value[0]);
            south = Math.min(south, value[1]);
            east = Math.max(east, value[0]);
            north = Math.max(north, value[1]);
            return;
        }
        value.forEach(visit);
    };
    visit(area.geometry.coordinates);
    return [west, south, east, north];
}
export function bboxContainsPoint(bbox, point) {
    return point.longitude >= bbox[0] && point.longitude <= bbox[2]
        && point.latitude >= bbox[1] && point.latitude <= bbox[3];
}
export function bboxesOverlap(left, right) {
    return left[0] <= right[2] && left[2] >= right[0] && left[1] <= right[3] && left[3] >= right[1];
}
function pointOnSegment(x, y, ax, ay, bx, by) {
    const squaredLength = (bx - ax) ** 2 + (by - ay) ** 2;
    if (squaredLength < 1e-20)
        return Math.abs(x - ax) < 1e-10 && Math.abs(y - ay) < 1e-10;
    const cross = (x - ax) * (by - ay) - (y - ay) * (bx - ax);
    if (Math.abs(cross) > 1e-10)
        return false;
    const dot = (x - ax) * (bx - ax) + (y - ay) * (by - ay);
    if (dot < 0)
        return false;
    return dot <= squaredLength;
}
function ringSegments(ring) {
    const result = [];
    for (let index = 0; index < ring.length - 1; index++) {
        const a = ring[index], b = ring[index + 1];
        if (!a || !b)
            continue;
        result.push({ index, a, b, minX: Math.min(a[0], b[0]), maxX: Math.max(a[0], b[0]), minY: Math.min(a[1], b[1]), maxY: Math.max(a[1], b[1]) });
    }
    return result.sort((left, right) => left.minX - right.minX || left.minY - right.minY || left.index - right.index);
}
function ringPairIntersects(leftRing, rightRing, intersection) {
    const left = ringSegments(leftRing), right = ringSegments(rightRing);
    let firstRight = 0;
    for (const candidate of left) {
        while (firstRight < right.length && right[firstRight].maxX < candidate.minX)
            firstRight++;
        for (let index = firstRight; index < right.length; index++) {
            const other = right[index];
            if (other.minX > candidate.maxX)
                break;
            if (other.maxY < candidate.minY || other.minY > candidate.maxY)
                continue;
            if (intersection(candidate.a, candidate.b, other.a, other.b))
                return true;
        }
    }
    return false;
}
function orientation(a, b, c) {
    return (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
}
export function segmentsIntersect(a, b, c, d) {
    const abC = orientation(a, b, c);
    const abD = orientation(a, b, d);
    const cdA = orientation(c, d, a);
    const cdB = orientation(c, d, b);
    if (((abC > 0 && abD < 0) || (abC < 0 && abD > 0)) && ((cdA > 0 && cdB < 0) || (cdA < 0 && cdB > 0)))
        return true;
    return Math.abs(abC) <= 1e-10 && pointOnSegment(c[0], c[1], a[0], a[1], b[0], b[1])
        || Math.abs(abD) <= 1e-10 && pointOnSegment(d[0], d[1], a[0], a[1], b[0], b[1])
        || Math.abs(cdA) <= 1e-10 && pointOnSegment(a[0], a[1], c[0], c[1], d[0], d[1])
        || Math.abs(cdB) <= 1e-10 && pointOnSegment(b[0], b[1], c[0], c[1], d[0], d[1]);
}
export function ringSelfIntersects(ring) {
    const segments = ringSegments(ring), lastSegment = ring.length - 2;
    for (let left = 0; left < segments.length; left++) {
        const candidate = segments[left];
        for (let right = left + 1; right < segments.length; right++) {
            const other = segments[right];
            if (other.minX > candidate.maxX)
                break;
            if (other.maxY < candidate.minY || other.minY > candidate.maxY)
                continue;
            if (Math.abs(candidate.index - other.index) === 1 || (Math.min(candidate.index, other.index) === 0 && Math.max(candidate.index, other.index) === lastSegment))
                continue;
            if (segmentsIntersect(candidate.a, candidate.b, other.a, other.b))
                return true;
        }
    }
    return false;
}
function pointInRing(point, ring) {
    const x = point.longitude;
    const y = point.latitude;
    let inside = false;
    for (let index = 0, previous = ring.length - 1; index < ring.length; previous = index++) {
        const currentPoint = ring[index];
        const previousPoint = ring[previous];
        if (!currentPoint || !previousPoint || currentPoint.length < 2 || previousPoint.length < 2)
            continue;
        const [xi, yi] = currentPoint;
        const [xj, yj] = previousPoint;
        if (pointOnSegment(x, y, xi, yi, xj, yj))
            return true;
        const intersects = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
        if (intersects)
            inside = !inside;
    }
    return inside;
}
function pointInPolygon(point, geometry) {
    const [outer, ...holes] = geometry.coordinates;
    if (!outer || !pointInRing(point, outer))
        return false;
    return !holes.some((hole) => pointInRing(point, hole));
}
export function containsPoint(area, point) {
    if (!bboxContainsPoint(area.bbox ?? calculateAreaBbox(area), point))
        return false;
    if (area.geometry.type === "Polygon")
        return pointInPolygon(point, area.geometry);
    return area.geometry.coordinates.some((coordinates) => pointInPolygon(point, { type: "Polygon", coordinates }));
}
function rings(area) {
    return area.geometry.type === "Polygon" ? area.geometry.coordinates : area.geometry.coordinates.flat();
}
export function areasIntersect(left, right) {
    if (!bboxesOverlap(left.bbox ?? calculateAreaBbox(left), right.bbox ?? calculateAreaBbox(right)))
        return false;
    for (const leftRing of rings(left)) {
        for (const rightRing of rings(right)) {
            if (ringPairIntersects(leftRing, rightRing, segmentsIntersect))
                return true;
        }
    }
    const leftOuter = left.geometry.type === "Polygon" ? [left.geometry.coordinates[0]] : left.geometry.coordinates.map((polygon) => polygon[0]);
    const rightOuter = right.geometry.type === "Polygon" ? [right.geometry.coordinates[0]] : right.geometry.coordinates.map((polygon) => polygon[0]);
    return leftOuter.some((ring) => ring?.[0] && containsPoint(right, { longitude: ring[0][0], latitude: ring[0][1] }))
        || rightOuter.some((ring) => ring?.[0] && containsPoint(left, { longitude: ring[0][0], latitude: ring[0][1] }));
}
function strictlyContainsPoint(area, point) {
    if (!containsPoint(area, point))
        return false;
    return !rings(area).some(ring => ring.some((position, index) => {
        const next = ring[(index + 1) % ring.length];
        return next && pointOnSegment(point.longitude, point.latitude, position[0], position[1], next[0], next[1]);
    }));
}
function segmentsProperlyIntersect(a, b, c, d) {
    const abC = orientation(a, b, c), abD = orientation(a, b, d), cdA = orientation(c, d, a), cdB = orientation(c, d, b), epsilon = 1e-10;
    return ((abC > epsilon && abD < -epsilon) || (abC < -epsilon && abD > epsilon))
        && ((cdA > epsilon && cdB < -epsilon) || (cdA < -epsilon && cdB > epsilon));
}
/** Positive-area overlap for sibling diagnostics; a shared boundary alone is not overlap. */
export function areasInteriorOverlap(left, right) {
    const leftBox = left.bbox ?? calculateAreaBbox(left), rightBox = right.bbox ?? calculateAreaBbox(right);
    if (leftBox[0] >= rightBox[2] || leftBox[2] <= rightBox[0] || leftBox[1] >= rightBox[3] || leftBox[3] <= rightBox[1])
        return false;
    for (const leftRing of rings(left))
        for (const rightRing of rings(right)) {
            if (ringPairIntersects(leftRing, rightRing, segmentsProperlyIntersect))
                return true;
        }
    const probes = (area) => rings(area).flatMap(ring => ring.flatMap((position, index) => {
        const next = ring[index + 1];
        return next ? [{ longitude: position[0], latitude: position[1] }, { longitude: (position[0] + next[0]) / 2, latitude: (position[1] + next[1]) / 2 }] : [];
    }));
    return probes(left).some(point => strictlyContainsPoint(right, point)) || probes(right).some(point => strictlyContainsPoint(left, point));
}
export function resolveAreas(areas, point) {
    return areas
        .filter((area) => containsPoint(area, point))
        .sort((left, right) => (right.priority ?? 0) - (left.priority ?? 0) || left.id.localeCompare(right.id));
}
//# sourceMappingURL=spatial.js.map